import React, { useState, useEffect, useReducer } from "react";
import { toast } from "../../services/toast";

import { useNavigate } from "react-router-dom";

import { makeStyles } from 'tss-react/mui';
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import PeopleIcon from "@mui/icons-material/People";
import DownloadIcon from "@mui/icons-material/GetApp";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

import chat_plantilla from "../../assets/chat_plantilla.xlsx";
import { socketConnection } from "../../services/socket";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTLISTS") {
    const contactLists = action.payload;
    const newContactLists = [];

    contactLists.forEach((contactList) => {
      const contactListIndex = state.findIndex((u) => u.id === contactList.id);
      if (contactListIndex !== -1) {
        state[contactListIndex] = contactList;
      } else {
        newContactLists.push(contactList);
      }
    });

    return [...state, ...newContactLists];
  }

  if (action.type === "UPDATE_CONTACTLIST") {
    const contactList = action.payload;
    const contactListIndex = state.findIndex((u) => u.id === contactList.id);

    if (contactListIndex !== -1) {
      state[contactListIndex] = contactList;
      return [...state];
    } else {
      return [contactList, ...state];
    }
  }

  if (action.type === "DELETE_CONTACTLIST") {
    const contactListId = action.payload;

    const contactListIndex = state.findIndex((u) => u.id === contactListId);
    if (contactListIndex !== -1) {
      state.splice(contactListIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
    flexWrap: "wrap",
  },
  acciones: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  buscador: {
    minWidth: 260,
    "& .MuiOutlinedInput-root": {
      backgroundColor:
        theme.palette.mode === "light"
          ? theme.palette.background.paper
          : "rgba(255,255,255,0.04)",
    },
  },
  btnNueva: {
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  tabla: {
    "& th": {
      fontWeight: 700,
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
  },
  tablaCabecera: {
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : "rgba(255,255,255,0.05)",
  },
  celdaNombre: {
    width: "40%",
    minWidth: 220,
  },
  nombre: {
    fontWeight: 600,
  },
  num: {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  invalidos: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  celdaAcciones: {
    whiteSpace: "nowrap",
    "& a": {
      color: "inherit",
      textDecoration: "none",
    },
  },
  nunca: {
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    fontSize: 12,
  },
  vacio: {
    padding: theme.spacing(4, 0),
    color: theme.palette.text.secondary,
    fontSize: 13.5,
  },
}));

const ContactLists = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [verArchivadas, setVerArchivadas] = useState("no");
  const [contactLists, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, verArchivadas]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContactLists = async () => {
        try {
          const { data } = await api.get("/contact-lists/", {
            params: { searchParam, pageNumber, archived: verArchivadas },
          });
          dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContactLists();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, verArchivadas, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-ContactList`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const handleDeleteContactList = async (contactListId) => {
    try {
      await api.delete(`/contact-lists/${contactListId}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContactList(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContacts = (id) => {
    navigate(`/contact-lists/${id}/contacts`);
  };

  const handleArchive = async (contactList) => {
    try {
      await api.put(`/contact-lists/${contactList.id}/archive`, {
        archived: !contactList.archived,
      });
      dispatch({ type: "DELETE_CONTACTLIST", payload: contactList.id });
      toast.success(
        contactList.archived ? "Lista desarchivada." : "Lista archivada."
      );
    } catch (err) {
      toastError(err);
    }
  };

  const fecha = (v) =>
    v ? new Date(v).toLocaleDateString("es-PE") : "—";

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal
        title={
          deletingContactList &&
          `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${
            deletingContactList.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />
      <MainHeader>
        <div className={classes.header}>
          <Title>{i18n.t("contactLists.title")}</Title>

          <div className={classes.acciones}>
            <TextField
              className={classes.buscador}
              size="small"
              variant="outlined"
              placeholder={i18n.t("contacts.searchPlaceholder")}
              value={searchParam}
              onChange={handleSearch}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: searchParam ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setSearchParam("")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />

            <Tooltip
              title={
                verArchivadas === "yes"
                  ? "Volver a las listas activas"
                  : "Ver listas archivadas"
              }
            >
              <IconButton
                onClick={() =>
                  setVerArchivadas(verArchivadas === "yes" ? "no" : "yes")
                }
                color={verArchivadas === "yes" ? "primary" : "default"}
              >
                <ArchiveIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={handleOpenContactListModal}
              className={classes.btnNueva}
            >
              {i18n.t("contactLists.buttons.add")}
            </Button>
          </div>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small" className={classes.tabla}>
          <TableHead className={classes.tablaCabecera}>
            <TableRow>
              <TableCell align="left">
                {i18n.t("contactLists.table.name")}
              </TableCell>
              <TableCell align="right">
                {i18n.t("contactLists.table.contacts")}
              </TableCell>
              <TableCell align="right">
                {i18n.t("contactLists.table.isvalidated")}
              </TableCell>
              <TableCell align="right">
                {i18n.t("contactLists.table.isNotValidated")}
              </TableCell>
              <TableCell align="right">Campañas</TableCell>
              <TableCell align="right">Último uso</TableCell>
              <TableCell align="right">Creada</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contactLists.map((contactList) => (
                <TableRow key={contactList.id} hover>
                  <TableCell align="left" className={classes.celdaNombre}>
                    <span className={classes.nombre}>{contactList.name}</span>
                    {contactList.archived && (
                      <Chip
                        label="Archivada"
                        size="small"
                        variant="outlined"
                        sx={{ ml: 0.75, height: 20, fontSize: 11 }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="right" className={classes.num}>
                    {contactList.contactsCount || 0}
                  </TableCell>
                  <TableCell align="right" className={classes.num}>
                    {contactList.validContactsCount || 0}
                  </TableCell>
                  <TableCell align="right" className={classes.num}>
                    {Number(contactList.invalidContactsCount) > 0 ? (
                      <span className={classes.invalidos}>
                        {contactList.invalidContactsCount}
                      </span>
                    ) : (
                      0
                    )}
                  </TableCell>

                  <TableCell align="right" className={classes.num}>
                    {Number(contactList.campaignsCount) > 0 ? (
                      contactList.campaignsCount
                    ) : (
                      <span className={classes.nunca}>Sin usar</span>
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.num}>
                    {contactList.lastUsedAt ? (
                      fecha(contactList.lastUsedAt)
                    ) : (
                      <span className={classes.nunca}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.num}>
                    {fecha(contactList.createdAt)}
                  </TableCell>

                  <TableCell align="right" className={classes.celdaAcciones}>
                    <Tooltip title="Descargar plantilla de importación">
                      <a href={chat_plantilla} download="chat_plantilla.xlsx">
                        <IconButton size="small">
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </a>
                    </Tooltip>

                    <Tooltip title="Ver contactos">
                      <IconButton
                        size="small"
                        onClick={() => goToContacts(contactList.id)}
                      >
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEditContactList(contactList)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={
                        contactList.archived
                          ? "Desarchivar"
                          : "Archivar (deja de aparecer aquí y en campañas)"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleArchive(contactList)}
                      >
                        {contactList.archived ? (
                          <UnarchiveIcon fontSize="small" />
                        ) : (
                          <ArchiveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingContactList(contactList);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {!loading && contactLists.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <div className={classes.vacio}>
                      {searchParam
                        ? `Ninguna lista coincide con "${searchParam}".`
                        : verArchivadas === "yes"
                        ? "No hay listas archivadas."
                        : "Todavía no has creado ninguna lista de contactos."}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {loading && <TableRowSkeleton columns={8} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ContactLists;
